import React, { useState, useCallback, useEffect, useContext } from "react";
import { X } from "lucide-react";
import { useView } from "../propertiesPage/context/ViewContext";
import { Context } from "services";



interface RangeSearchProps {
  onValueChange: (value: string | null) => void;
}

const RangeSearch: React.FC<RangeSearchProps> = ({ onValueChange }) => {
  const [fromValue, setFromValue] = useState<string>("");
  const [toValue, setToValue] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { clearRangeDateSearch, setClearRangeDateSearch } = useView();
  const { searchForm }: any = useContext(Context);


  const formatIndianCurrency = (value: string): string => {
    if (value === "") return "";
    const numValue = parseFloat(value);
    return `₹ ${new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue)}`;
  };

  const parseFormattedValue = (value: string): number => {
    return Math.round(parseFloat(value.replace(/[^\d.]/g, '')));
  };

  const validateAndPrepareSearch = useCallback(() => {
    const fromValueNumber = fromValue === '' ? 0 : parseFormattedValue(fromValue);
    const toValueNumber = toValue === '' ? 0 : parseFormattedValue(toValue);
    if (fromValueNumber > toValueNumber) {
      setErrorMessage("From value cannot be greater than To value");
      return null;
    } else {
      setErrorMessage("");
    }
    const adjustedFromValue = Math.floor(fromValueNumber / 100000);
    const adjustedToValue = Math.floor(toValueNumber / 100000);
    return `${adjustedFromValue}-${adjustedToValue}`;
  }, [fromValue, toValue]);

  useEffect(() => {
    const value = validateAndPrepareSearch();
    onValueChange(value);
  }, [fromValue, toValue, validateAndPrepareSearch, onValueChange]);

  useEffect(() => {
    if (clearRangeDateSearch) {
      handleReset();
      setClearRangeDateSearch(false);
    }
  }, [clearRangeDateSearch, setClearRangeDateSearch]);

  const handleFromValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    const formattedValue = formatIndianCurrency(rawValue);
    setFromValue(formattedValue);
  };

  const handleToValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    const formattedValue = formatIndianCurrency(rawValue);
    setToValue(formattedValue);
  };



  const handleReset = () => {
    searchForm.setField("reserve_price", "");
    setFromValue("");
    setToValue("");
    setErrorMessage("");
    onValueChange(null);
  };

  const showResetIcon = fromValue || toValue;

  return (
    <div className="py-2 px-1 flex flex-col gap-y-1">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium">Select Price Range</h3>
        {showResetIcon && (
          <button onClick={handleReset} className="text-gray-500 hover:text-gray-700">
            <X size={13} />
          </button>
        )}
      </div>
      <div className="flex flex-col justify-center gap-y-2">
        <div>
          <div className="text-xs font-medium text-gray-600">From</div>
          <input
            type="text"
            inputMode="numeric"
            value={fromValue}
            onChange={handleFromValueChange}
            className="p-2 border border-gray-300 rounded h-9 w-full font-normal text-xs text-blue-600 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500"
            placeholder="₹ 1,00,000"
          />
        </div>
        <div>
          <div className="text-xs font-medium text-gray-600">To</div>
          <input
            type="text"
            inputMode="numeric"
            value={toValue}
            onChange={handleToValueChange}
            className="p-2 border border-gray-300 rounded h-9 w-full font-normal text-xs text-blue-600 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500"
            placeholder="₹ 1,00,000"
          />
        </div>
      </div>
      {errorMessage && (
        <p className="text-red-500 text-sm mt-2">{errorMessage}</p>
      )}
    </div>
  );
};

export default RangeSearch;